import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { QueryKeyConfig } from '../../../api/api-helper';
import { FormError } from '../../../components/hook-form/hook-form-error';
import AutoCompleteMultiSelect from './autocomplete-multi-select.component';

interface AutoCompleteSelectFieldDynamicProps {
  options?: any[];
  getQueryKeyConfig?: (inputData: any, getValues: any) => QueryKeyConfig;
  label: string;
  name: string;
  watchKeys?: string[];
  size?: number;
  onChange?: any;
  multiline?: boolean;
  optionsValueKey: string;
  optionsLabelKey: string;
  disabled?: boolean;
  disableCloseOnSelect?: boolean;
  className?: string;
  onChangeWithPrevious?: any;
  required?: boolean;
}

const mergeDataItems = (data1: any[], data2: any[], key: string) => {
  const dataArray1 = Array.isArray(data1) ? data1 : [];
  const dataArray2 = Array.isArray(data2) ? data2 : [];
  const result = dataArray1?.filter((d) => !dataArray2?.some((d2) => d2[key] === d[key]));
  return [...result, ...dataArray2];
};

export const FormAutoCompleteMultiSelectDynamic = ({
  name,
  label,
  size,
  watchKeys,
  multiline,
  options,
  onChange,
  optionsValueKey,
  optionsLabelKey,
  disabled,
  disableCloseOnSelect,
  className,
  getQueryKeyConfig,
  onChangeWithPrevious,
  required,
}: AutoCompleteSelectFieldDynamicProps): any => {
  const { control, getValues } = useFormContext();
  const [completeData, setCompleteData] = useState<any[]>([]);

  const previousValueRef = useRef<any[]>(getValues(name));

  useWatch({
    name: watchKeys || [],
  });

  const query = getQueryKeyConfig?.(
    watchKeys?.reduce((acc: any, key) => ({ ...acc, [key]: getValues(key) }), {}),
    getValues
  );

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(query, !!query);

  useEffect(() => {
    const mergedData = mergeDataItems(data?.data ?? [], options ?? [], optionsValueKey);
    setCompleteData(mergedData);
  }, [data, options]);

  const handleChange = (value: any) => {
    onChange(value);
    onChangeWithPrevious?.(value, previousValueRef.current);
    previousValueRef.current = getValues(name);
  };

  return (
    <>
      <LoadingIndicator isLoading={isLoading || isFetching} />
      <Controller
        control={control}
        rules={{
          required: required ? `${label} is required` : false, // Apply required validation if `required` is true
        }}
        name={name}
        render={({ field }) => {
          if (completeData.length > 0) {
            return (
              <AutoCompleteMultiSelect
                options={completeData}
                label={label}
                multiline={multiline}
                size={size}
                onChange={(value: any) => {
                  field.onChange(value);
                  handleChange(value);
                }}
                selectedValues={Array.isArray(field.value) ? field.value : []}
                optionsLabelKey={optionsLabelKey}
                optionsValueKey={optionsValueKey}
                disabled={disabled}
                disableCloseOnSelect={disableCloseOnSelect}
              />
            );
          }
          return (
            <AutoCompleteMultiSelect
              options={completeData}
              label={label}
              multiline={multiline}
              size={size}
              onChange={(value: any) => {
                field.onChange(value);
                handleChange(value);
              }}
              selectedValues={Array.isArray(field.value) ? field.value : []}
              optionsLabelKey={optionsLabelKey}
              optionsValueKey={optionsValueKey}
              disabled={disabled}
              disableCloseOnSelect={disableCloseOnSelect}
            />
          );
        }}
      />
      <FormError name={name} />
    </>
  );
};

FormAutoCompleteMultiSelectDynamic.defaultProps = {
  optionsValueKey: 'id',
  defaultItemKey: 'name',
  onChange: () => {
    return;
  },
};
